import { snapshotIsVendor, snapshotIsVisitor } from './employeeTypeUi';
import { getPersonFaces } from './api';

/** @typedef {'forced' | 'event' | 'facesPerson'} PersonLinkRouteSource */

/** True when person_id is all digits (e.g. HRMS numeric IDs). */
export function isIntegerPersonId(personId) {
  return /^\d+$/.test(String(personId ?? '').trim());
}

/**
 * @param {unknown} row
 * @param {{ routeSource?: PersonLinkRouteSource, detailKind?: string, name?: string, personId?: string }} [options]
 */
export function buildPersonLinkMeta(row, options = {}) {
  const source = row && typeof row === 'object' ? row : {};
  const name =
    options.name ??
    source.name ??
    source.person_name ??
    source.recognized_name ??
    '';
  const personId =
    options.personId ??
    source.person_id ??
    source.recognized_person_id ??
    null;
  const meta = {
    name: name != null ? String(name) : '',
    person_id: personId != null ? String(personId) : '',
    employee_type: source.employee_type ?? null,
  };
  if (options.detailKind) {
    meta.detailKind = options.detailKind;
    meta.routeSource = 'forced';
    return meta;
  }
  meta.routeSource = options.routeSource ?? 'event';
  return meta;
}

/** @param {{ detailKind?: string, employee_type?: unknown }} [personMeta] */
export function isVendorPersonRecord(personMeta = {}) {
  const detailKind = String(personMeta.detailKind || '').trim().toLowerCase();
  if (detailKind === 'vendor') return true;
  if (detailKind === 'employee') return false;
  return snapshotIsVendor(personMeta.employee_type);
}

/** @param {{ detailKind?: string, employee_type?: unknown }} [personMeta] */
export function isVisitorPersonRecord(personMeta = {}) {
  const detailKind = String(personMeta.detailKind || '').trim().toLowerCase();
  if (detailKind === 'visitor') return true;
  if (detailKind === 'employee' || detailKind === 'vendor') return false;
  return snapshotIsVisitor(personMeta.employee_type);
}

/** @param {unknown} personId @param {{ detailKind?: string, employee_type?: unknown }} [personMeta] */
export function buildPersonDetailsUrl(personId, personMeta = {}) {
  const pid = personId != null ? String(personId).trim() : '';
  if (!pid) return '';
  const today = new Date().toISOString().split('T')[0];
  const base = isVendorPersonRecord(personMeta)
    ? '/vendor'
    : isVisitorPersonRecord(personMeta)
    ? '/visitor'
    : '/employee';
  return `${base}/${pid}?start_date=${today}&end_date=${today}`;
}

/** @param {unknown[]} faces */
export function isVendorFromPersonFaces(faces) {
  if (!Array.isArray(faces) || !faces.length) return false;
  return faces.some((face) => snapshotIsVendor(face?.employee_type));
}

/** @param {unknown[]} faces */
export function isVisitorFromPersonFaces(faces) {
  if (!Array.isArray(faces) || !faces.length) return false;
  return faces.some((face) => snapshotIsVisitor(face?.employee_type));
}

/**
 * @param {unknown} personId
 * @param {{ detailKind?: string, employee_type?: unknown, routeSource?: PersonLinkRouteSource }} [personMeta]
 */
export async function resolvePersonDetailsUrl(personId, personMeta = {}) {
  const pid = personId != null ? String(personId).trim() : '';
  if (!pid) return '';

  const routeSource = personMeta.routeSource || 'event';
  if (routeSource === 'forced' || personMeta.detailKind) {
    return buildPersonDetailsUrl(pid, personMeta);
  }

  if (routeSource === 'facesPerson') {
    try {
      const faces = await getPersonFaces(pid);
      const detailKind = isVendorFromPersonFaces(faces)
        ? 'vendor'
        : isVisitorFromPersonFaces(faces)
        ? 'visitor'
        : 'employee';
      return buildPersonDetailsUrl(pid, { detailKind });
    } catch {
      return buildPersonDetailsUrl(pid, { detailKind: 'employee' });
    }
  }

  return buildPersonDetailsUrl(pid, personMeta);
}
